import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Route, Ban, Wallet, Smartphone } from 'lucide-react';
import Seo from '../components/Seo';
import HeaderSection from './HeaderSection';
import Footer from './Footer';
import ScrollToTop from './ScrollToTop';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.45, delay },
});

const PRINCIPLES = [
  {
    Icon: Route,
    title: 'Distance & time based',
    desc: "Fares are calculated from the actual distance and time of your trip, not a flat guess. What the app quotes before you book is what you pay.",
  },
  {
    Icon: Ban,
    title: 'Near-zero surge',
    desc: 'We keep surge pricing minimal even during peak hours and bad weather, instead of multiplying fares the way many ride apps do.',
  },
  {
    Icon: Wallet,
    title: 'Zero commission for drivers',
    desc: "As a launch offer, TotoWala takes no commission from drivers. No platform fees, no hidden charges deducted from a driver's earnings.",
  },
  {
    Icon: Smartphone,
    title: 'Flexible payments',
    desc: 'Pay by cash, UPI, debit/credit card, or digital wallet. All in-app payments are processed securely.',
  },
];

const Pricing = () => {
  return (
    <>
      <Seo
        title="Pricing - TotoWala | Transparent Ride Fares Across India"
        description="How TotoWala pricing works: distance and time based fares, near-zero surge pricing, zero commission for drivers, and flexible payment options across India."
        canonical="/pricing"
      />
      <div className="min-h-screen bg-white dark:bg-dark-bg">
        <HeaderSection />

        <section className="bg-surface-alt dark:bg-dark-surface-alt py-12 lg:py-14 px-4">
          <div className="max-w-[760px] mx-auto text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}
              className="font-display text-3xl sm:text-4xl font-semibold leading-tight text-ink dark:text-dark-ink mb-4"
            >
              Pricing
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.06 }}
              className="text-base text-ink-soft dark:text-dark-ink-soft leading-relaxed max-w-lg mx-auto"
            >
              TotoWala does not publish a fixed rate card — every trip's fare depends on
              pickup, drop, distance, and time of day, and is shown in the app before you
              confirm a booking. Here is how our pricing model works and why it is
              different from most ride apps.
            </motion.p>
          </div>
        </section>

        <section className="bg-white dark:bg-dark-bg py-12 lg:py-14 px-4">
          <div className="max-w-[1000px] mx-auto grid sm:grid-cols-2 gap-5">
            {PRINCIPLES.map(({ Icon, title, desc }, i) => (
              <motion.div
                key={title} {...fadeUp(i * 0.06)}
                className="p-6 rounded-card bg-surface-alt dark:bg-dark-surface border border-line dark:border-dark-line"
              >
                <div className="w-11 h-11 rounded-control bg-primary-tint dark:bg-primary/10 border border-primary/25 flex items-center justify-center mb-4">
                  <Icon size={20} className="text-primary" />
                </div>
                <h2 className="text-base font-bold text-ink dark:text-dark-ink mb-2">{title}</h2>
                <p className="text-sm text-ink-soft dark:text-dark-ink-soft leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        <section className="bg-surface-alt dark:bg-dark-surface-alt py-12 lg:py-14 px-4">
          <div className="max-w-[760px] mx-auto text-center">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-ink dark:text-dark-ink mb-3">
              Ride types
            </h2>
            <p className="text-base text-ink-soft dark:text-dark-ink-soft leading-relaxed mb-8">
              TotoWala offers four ride types, each priced according to the principles
              above: Toto (e-rickshaw), Bike taxi, Cab, and Auto rickshaw. Fare and ride
              type availability vary by city.
            </p>
            <p className="text-sm text-ink-muted">
              For the exact fare of a specific trip, open the TotoWala app, enter your
              pickup and destination, and the quote is shown before you book. Questions
              about a charged fare can be sent to our{' '}
              <Link to="/contact" className="text-primary underline">contact</Link> page.
            </p>
          </div>
        </section>

        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
};

export default Pricing;
